import { Dropdown, message, MenuProps, Skeleton } from 'antd';
import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { DownOutlined } from '@ant-design/icons';

import { EPButton } from '@/components';
import { useStores } from '@/hooks/use-stores';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import HeaderContainer from './Header.styles';
import { getLastUpdatedInfo } from '../../PIDEditing.utils';
import { getMenuItems } from './Header.utils';

const Header = observer(({ diagramCoverOpacity }: { diagramCoverOpacity: number }) => {
  const { stationStore, pidEditStore } = useStores();
  const { t } = useTranslation();
  const lastUpdatedInfo = useMemo(
    () => getLastUpdatedInfo(pidEditStore.lastActivatedUserInfo, t),
    [pidEditStore.lastActivatedUserInfo, t],
  );

  const menuItems = useMemo(() => getMenuItems(t), [t]);

  useEffect(() => {
    if (stationStore.stationStoreUserError) {
      pidEditStore.setIsInEditMode(false);
      pidEditStore.toggleEditMode(false);
      message.error(t('Error loading draft. Please try again.'));
    } else if (pidEditStore.isInEditMode && !stationStore.stationStoreUserError) {
      message.info(t('Click a pipe to add sensors/filters/valves.'));
    }
  }, [stationStore.stationStoreUserError, pidEditStore.isInEditMode]);

  const handleMenuClick: MenuProps['onClick'] = async e => {
    switch (e.key) {
      case 'save-draft':
        await pidEditStore.disableEditMode();
        message.info(t('Edit mode disabled.'));
        break;
      case 'discard-draft':
        await pidEditStore.resetDraft();
        await pidEditStore.disableEditMode();
        message.success(t('Draft Discarded, Edit mode disabled.'));
        break;
      default:
        break;
    }
  };

  const handleSaveChanges = () => {
    pidEditStore.showMenu(MenuType.SaveChanges, { x: 0, y: 0 });
  };

  return (
    <HeaderContainer>
      <Skeleton active loading={!diagramCoverOpacity} paragraph={false} title={{ width: 120 }}>
        <span className="map-editor-title">{t('mapEditor')}</span>
      </Skeleton>
      <div className="action-buttons">
        {diagramCoverOpacity ? (
          <>
            <span className="last-updated-info">{lastUpdatedInfo}</span>
            <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
              <EPButton>
                <span>{t('Exit Edit Mode')}</span>
                <DownOutlined />
              </EPButton>
            </Dropdown>
            <EPButton onClick={handleSaveChanges} type="default">
              {t('Save Changes')}
            </EPButton>
          </>
        ) : (
          <>
            <Skeleton.Button active /> <Skeleton.Input active />
          </>
        )}
      </div>
    </HeaderContainer>
  );
});

export default Header;
